import { useState, FormEvent } from 'react';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthCard } from '@/components/auth';
import { useAuthFlow } from '@/hooks/useAuthFlow';
import { isValidEmail } from '@/hooks/usePasswordValidation';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';

export default function ForgotPassword() {
  const [, setLocation] = useLocation();
  const { forgotPassword, isSubmitting, error, clearError } = useAuthFlow();

  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState<string>();

  const validateForm = (): boolean => {
    if (!email) {
      setValidationError('Email is required');
      return false;
    }
    if (!isValidEmail(email)) {
      setValidationError('Please enter a valid email address');
      return false;
    }
    setValidationError(undefined);
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) {
      return;
    }

    const result = await forgotPassword(email);

    if (result.success) {
      // Navigate to reset password page (email stored in useAuthFlow)
      setLocation('/reset-password');
    }
  };

  return (
    <AuthCard
      title="Reset your password"
      description="Enter your email and we'll send you a reset code"
      footer={
        <p>
          Remember your password?{' '}
          <Link href="/login" className="text-amber-600 hover:text-amber-700 font-medium">
            Sign in
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setLocation('/login')}
          className="mb-2 -ml-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to login
        </Button>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (validationError) {
                setValidationError(undefined);
              }
            }}
            placeholder="you@example.com"
            autoComplete="email"
            disabled={isSubmitting}
            className={validationError ? 'border-red-500' : ''}
          />
          {validationError && (
            <p className="text-sm text-red-500" role="alert" aria-live="polite">{validationError}</p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending reset code...
            </>
          ) : (
            'Send Reset Code'
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          We&apos;ll send a verification code to your email address.
          Check your spam folder if you don&apos;t see it.
        </p>
      </form>
    </AuthCard>
  );
}
